import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { setDraftCount, setHasPublishedPostings } from '~/actions/PermanentJobs/PermanentJobs';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Box, Button, Column, Row } from '@tempmee/react';
import { CenteredSpinner, CollapsibleCard, ConfirmPopup } from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import { useGetPostingPlans } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useAppSelector, useNavigationDetector, useToggle } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import WarningIcon from '~/images/warning-icon.svg';
import { routes } from '~/routes';
import {
  CreateOnsiteInterviewSteps,
  TCreateOnsiteInterviewPageLocationState,
} from '../CreateOnsiteInterview';
import {
  BasicInfoPreview,
  EarningsBenefitsPreview,
  InfoSection,
  JobDescriptionPreview,
  JobDescriptionReview,
  JobInformationReview,
  ScheduleAndPayReview,
} from '../JobDetailsForm';
import { SchedulePreview } from '../JobDetailsForm/components/ScheduleInfo';
import { formatExpectedDays } from '../JobDetailsForm/utils/formatters';
import { TermsConditionAddendumPopup } from '../components';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageLocationState,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import { HiringPlanCard, PaymentDetailsPreview, PermanentJobsTips } from './components';
import { useDraftJobPost, useRequireTnCAddendum } from './hooks';
import { usePublishJobPost } from './hooks/usePublishJobPost';
import { firePermPostJobSubmitted, firePermPostJobAddInterviewsClicked } from '~/posthogs/events';

/**
 * Helper function to check if a navigation path belongs to the allowed flow routes
 */
const isPathInAllowedFlow = (pathname: string, currentId: string | undefined) => {
  if (!pathname || !currentId) return false;

  // Allow navigation to any path that starts with /permanent-jobs/postings/:id/create
  const allowedPattern = new RegExp(`^/permanent-jobs/postings/${currentId}/create`);

  return allowedPattern.test(pathname);
};

export const ReviewAndPaymentPage = () => {
  const dispatch = useDispatch();
  const draftCount = useAppSelector((state) => state.permanentJobs.draftCount);
  const navigate = useNavigate();
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();

  const [isPublishSucceed, controlsPublishSucceed] = useToggle();
  const [isShowExitModalConfirmation, controlsExitModalConfirmation] = useToggle();
  const [isShowPaymentError, controlsShowPaymentError] = useToggle();
  const [isShowTnCAddendumPopup, controlsShowTnCAddendumPopup] = useToggle();
  const [isJobDetailsOpen, controlsJobDetails] = useToggle();

  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  // Navigation blocking logic
  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: true, // Always block navigation from this page
    onNavigationAttempt: (targetLocation) => {
      const targetPath = targetLocation?.pathname;

      // Check if target is within the allowed flow
      if (isPathInAllowedFlow(targetPath, id)) {
        // Allow navigation within the flow without confirmation
        navigateToPending();
      } else {
        // Show confirmation modal for navigation outside the flow
        controlsExitModalConfirmation.setOn();
      }
    },
  });

  const { draft, isLoading } = useDraftJobPost(id);
  const {
    data: postingPlans,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
    error: errorPlans,
  } = useGetPostingPlans();

  // Select the first plan by default
  const selectedPlan = postingPlans?.[0];

  const {
    doPublish,
    isLoading: isPublishing,
    isError: isPublishError,
    error: publishError,
  } = usePublishJobPost({
    jobPostingId: id,
    planId: selectedPlan?.id,
    onSuccess: () => {
      // Track the job submission event
      if (id && draft?.professionType && selectedPlan?.finalPrice) {
        firePermPostJobSubmitted({
          postingId: id,
          professionType: draft.professionType,
          listingFee: selectedPlan.finalPrice.toFixed(2), // Format as "450.00"
        });
      }

      dispatch(setDraftCount(Math.max(0, (draftCount ?? 1) - 1)));
      dispatch(setHasPublishedPostings(true));
      controlsShowTnCAddendumPopup.setOff();
      controlsPublishSucceed.setOn();
    },
  });

  const { isAcceptingConsent, isConsentAccepted, doAcceptOfficeTnCPerm } = useRequireTnCAddendum({
    controls: controlsShowTnCAddendumPopup,
    onSucceedAcceptConsent: doPublish,
    jobPostingId: id,
  });

  useEffect(() => {
    if (isErrorPlans) {
      showErrorMessage({ error: errorPlans });
    }
  }, [isErrorPlans, errorPlans]);

  useEffect(() => {
    if (isPublishError) {
      if ((publishError as AxiosError<any>)?.response?.data?.errorCode === 'PAYMENT_FAILED') {
        controlsShowPaymentError.setOn();

        return;
      }

      showErrorMessage({ error: publishError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishError, isPublishError]);

  const handleEditClicked =
    (targetStep: CreatePermanentJobPostingSteps, scrollTo: CreatePermanentJobPostingFormSection) =>
    () => {
      if (isPublishing) {
        return;
      }

      navigate(`/permanent-jobs/postings/${id}/create/${targetStep}`, {
        state: {
          editMode: true,
          scrollTo,
        } satisfies TCreatePermanentJobPostingPageLocationState,
      });
    };

  const handleCloseSuccessPopup = () => {
    // Track Add Interviews clicked event
    if (id) {
      firePermPostJobAddInterviewsClicked({ postingId: id });
    }

    controlsPublishSucceed.setOff();
    allowNavigation();
    navigate(
      // eslint-disable-next-line max-len
      `/permanent-jobs/postings/${id}/onsite-interviews/create/${CreateOnsiteInterviewSteps.WorkDetails}`,
      {
        state: {
          isAfterJobPosting: true,
        } satisfies TCreateOnsiteInterviewPageLocationState,
      },
    );
  };

  const handlePostJobClicked = () => {
    if (!isConsentAccepted) {
      controlsShowTnCAddendumPopup.setOn();
      return;
    }

    doPublish();
  };

  if (isLoading || !draft || isLoadingPlans) {
    return <CenteredSpinner />;
  }

  const popups = (
    <>
      {isPublishSucceed && (
        <ConfirmPopup
          Icon={() => <AnimatedCheckCircle />}
          rightButtonAction={handleCloseSuccessPopup}
          rightButtonText="Set Up Interviews"
          rightButtonStyle={{ width: 296 }}
          title="Your Role Is Posted!"
          titleStyle={{ marginBottom: 10, marginTop: 14 }}
          description="Professionals are notified and can now see your job posting. Add interviews so they can start applying."
          descriptionStyle={{ marginBottom: 38, marginInline: 30, width: '90%' }}
        />
      )}

      {isShowExitModalConfirmation && (
        <ConfirmPopup
          Icon={() => <OutlineWarning01Icon size={80} color={colors.primary[500]} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            controlsExitModalConfirmation.setOff();
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Are You Sure You Want to Leave?"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="Your job isn't posted yet, but we saved it as a draft under Manage Jobs. Do you still want to leave this page?"
        />
      )}

      {isShowPaymentError && (
        <ConfirmPopup
          Icon={() => <img src={WarningIcon} alt="Warning" />}
          leftButtonStyle={{ width: 172 }}
          leftButtonAction={() => controlsShowPaymentError.setOff()}
          leftButtonText="Go Back"
          rightButtonStyle={{ width: 172 }}
          rightButtonAction={() => {
            allowNavigation();
            navigate(routes.paymentInformation);
          }}
          rightButtonText="Update Payment"
          title="Payment Failed - Job Not Posted"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="We couldn't process your payment. Your job has been saved in your drafts. Please update your payment method and try posting again."
        />
      )}

      <TermsConditionAddendumPopup
        isLoading={isAcceptingConsent || isPublishing}
        isOpen={isShowTnCAddendumPopup}
        onConfirm={doAcceptOfficeTnCPerm}
        jobPostingId={id}
      />
    </>
  );

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <Column gap={3} width="892px" className="self-center">
          <CollapsibleCard
            title="Review Job Details"
            isOpen={isJobDetailsOpen}
            onToggle={controlsJobDetails.toggle}
            backgroundColor="neutral_50"
            headerPadding="20px 26px"
            noShadow
            titleColor="neutral_600"
            headerStyle={{ alignItems: 'center' }}
          >
            <Column px={12} gap={3}>
              <JobInformationReview
                jobPosting={draft}
                onEditClick={handleEditClicked(
                  CreatePermanentJobPostingSteps.BasicInformation,
                  CreatePermanentJobPostingFormSection.GeneralJobInformation,
                )}
              />
              <ScheduleAndPayReview
                jobPosting={draft}
                onEditClick={handleEditClicked(
                  CreatePermanentJobPostingSteps.WorkDetails,
                  CreatePermanentJobPostingFormSection.Schedule,
                )}
              />
              <JobDescriptionReview
                description={draft.description || ''}
                onEditClick={handleEditClicked(
                  CreatePermanentJobPostingSteps.JobDescription,
                  CreatePermanentJobPostingFormSection.JobDescription,
                )}
              />
            </Column>
          </CollapsibleCard>

          <HiringPlanCard plan={selectedPlan} />

          <Row justify="center" className="mt-2">
            <Button
              onClick={handlePostJobClicked}
              isLoading={isAcceptingConsent || isPublishing}
              size="lg"
            >
              Post My Job
            </Button>
          </Row>
        </Column>

        {popups}
      </>
    );
  }

  return (
    <>
      <Column gap={2} width="892px" className="self-center">
        <BasicInfoPreview
          jobPosting={draft}
          onEditClick={handleEditClicked(
            CreatePermanentJobPostingSteps.BasicInformation,
            CreatePermanentJobPostingFormSection.GeneralJobInformation,
          )}
        />
        <InfoSection
          icon="calendar-solid"
          title="Schedule"
          rightActionIcon="edit-pencil"
          onRightActionPress={handleEditClicked(
            CreatePermanentJobPostingSteps.WorkDetails,
            CreatePermanentJobPostingFormSection.Schedule,
          )}
        >
          <SchedulePreview
            scheduleType={draft.scheduleType || ''}
            expectedDays={formatExpectedDays(draft.expectedDays || [])}
          />
        </InfoSection>
        <EarningsBenefitsPreview
          jobPosting={draft}
          onEditClick={handleEditClicked(
            CreatePermanentJobPostingSteps.WorkDetails,
            CreatePermanentJobPostingFormSection.EarningsAndBenefits,
          )}
        />
        <InfoSection
          icon="document-solid"
          title="Job Description"
          rightActionIcon="edit-pencil"
          onRightActionPress={handleEditClicked(
            CreatePermanentJobPostingSteps.JobDescription,
            CreatePermanentJobPostingFormSection.JobDescription,
          )}
        >
          <JobDescriptionPreview description={draft.description || ''} />
        </InfoSection>
        <Box height="6px" />
        <PermanentJobsTips />
        <Box height="20px" />
        <PaymentDetailsPreview plan={selectedPlan} />
        <Row justify="center" className="mt-2">
          <Button
            onClick={handlePostJobClicked}
            isLoading={isAcceptingConsent || isPublishing}
            size="lg"
          >
            Post Job
          </Button>
        </Row>
      </Column>

      {popups}
    </>
  );
};
